import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { Button } from './button.js';

export interface FileUploadProps {
  label: string;
  /** Welke bestandstypen de kiezer aanbiedt, bijvoorbeeld `image/*`. */
  accept?: string;
  hint?: ReactNode;
  error?: string | null;
  /** 0–100 tijdens het uploaden; `null` als er niets loopt. */
  progress?: number | null;
  /** De naam van het bestand dat op dit moment verwerkt wordt. */
  fileName?: string | null;
  disabled?: boolean;
  onSelect: (file: File) => void;
  /** Tonen zodra er iets loopt; de gebruiker moet kunnen stoppen. */
  onCancel?: () => void;
  /** Wat er nu staat: een miniatuur, een naam, of niets. */
  children?: ReactNode;
  /**
   * De opschriften van de knoppen. Ze staan hier als prop en niet als vaste tekst, omdat
   * deze component in drie talen gebruikt wordt en `packages/ui` geen vertalingen kent.
   */
  labels?: { choose?: string; camera?: string; cancel?: string };
  className?: string;
}

/**
 * Een bestand kiezen: slepen, klikken, of op een telefoon de camera.
 *
 * Twee dingen die op een fabrieksvloer het verschil maken:
 *
 * - **`capture` op de camera-knop.** Een medewerker heeft geen bestandsbeheerder op zijn
 *   telefoon en zoekt geen map; hij wil de camera. Die knop staat er apart, want de gewone
 *   kiezer opent bij veel Android-toestellen de galerij en niet de camera.
 * - **Voortgang en afbreken.** Een foto van tien megabyte duurt op een trage verbinding een
 *   halve minuut. Zonder balk denkt iemand dat het hangt en drukt hij opnieuw; zonder
 *   afbreken kan hij niets anders dan wachten.
 *
 * Slepen werkt op de desktop en is nooit de enige weg: elke sleepzone heeft een knop, want
 * slepen kan niet met een toetsenbord en niet op een telefoon.
 */
export function FileUpload({
  label,
  accept,
  hint,
  error,
  progress,
  fileName,
  disabled,
  onSelect,
  onCancel,
  children,
  labels,
  className,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const id = useId();
  const busy = progress !== null && progress !== undefined;

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0];
    if (file) onSelect(file);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (!disabled && !busy) handleFiles(event.dataTransfer.files);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span className="font-heading text-small font-medium text-text-secondary" id={`${id}-label`}>
        {label}
      </span>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        aria-labelledby={`${id}-label`}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border border-dashed p-4 text-center transition-colors',
          dragging ? 'border-brand-primary bg-brand-primary-tint' : 'border-border bg-surface',
          error && 'border-danger',
          disabled && 'opacity-50',
        )}
      >
        {children}

        {busy ? (
          <div className="w-full max-w-sm">
            <div
              role="progressbar"
              aria-valuenow={Math.round(progress as number)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={label}
              className="h-2 w-full overflow-hidden rounded-pill bg-background"
            >
              <div
                className="h-full rounded-pill bg-brand-primary transition-[width] duration-200"
                style={{ width: `${Math.min(100, Math.max(0, progress as number))}%` }}
              />
            </div>
            <p className="mt-2 text-small text-text-secondary">
              {fileName ? `${fileName} — ` : ''}
              {Math.round(progress as number)}%
            </p>
            {onCancel && (
              <Button variant="ghost" size="sm" className="mt-1" onClick={onCancel}>
                <X aria-hidden className="h-4 w-4" />
                {labels?.cancel ?? 'Stoppen'}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-inline">
            <Button
              variant="secondary"
              size="md"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              <Upload aria-hidden className="h-4 w-4" />
              {labels?.choose ?? 'Bestand kiezen'}
            </Button>

            {/* Alleen zichtbaar waar een camera zin heeft; op een desktop doet hij niets. */}
            {accept?.startsWith('image/') && (
              <Button
                variant="ghost"
                size="md"
                className="sm:hidden"
                disabled={disabled}
                onClick={() => cameraRef.current?.click()}
              >
                <Camera aria-hidden className="h-4 w-4" />
                {labels?.camera ?? 'Foto maken'}
              </Button>
            )}
          </div>
        )}

        {hint && !error && (
          <p id={`${id}-hint`} className="text-small text-text-secondary">
            {hint}
          </p>
        )}
      </div>

      {error && (
        <p id={`${id}-error`} role="alert" className="text-small text-danger">
          {error}
        </p>
      )}

      {/* Buiten het beeld, niet `display:none`: een verborgen invoer is niet aan te klikken. */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}
